import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable no-restricted-imports */ import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Link } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { useMutation } from '@tanstack/react-query';
import { Form, Space } from 'antd';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UserAPI } from 'api/user';
import { ErrorMessage, LinkText, UserInfo } from 'components/atoms/form-elements';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { Input } from 'components/facade/input';
import { Container } from 'components/organisms/login/login-layout';
import { ProductiveHeading04 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common';
import { logger } from 'services';
import { SuccessPage } from './success-page';
const RecoveryButton = styled(Button)`
  margin: var(--spacing-03) 0;
`;
const Heading = styled.div`
  ${ProductiveHeading04};
  margin-top: var(--layout-04);
`;
export const ForgotPasswordPage = ()=>{
    const [form] = Form.useForm();
    const [emailValue, setEmailValue] = useState('');
    const [isResetEmailSent, setIsResetEmailSent] = useState(false);
    // eslint-disable-next-line consistent-return
    const triggerResetMail = async ()=>{
        try {
            const { email } = await form.validateFields();
            return await UserAPI.triggerResetPasswordEmail(email).then(()=>{
                setEmailValue(email);
                setIsResetEmailSent(true);
            }).catch((err)=>Promise.reject(err));
        } catch (error) {
            logger.log('password reset failed', {
                error
            });
        }
    };
    const { mutateAsync: handleClick, isLoading: resetMailSending, isError: isResetFailed } = useMutation(triggerResetMail);
    const onSubmit = useCallback(()=>{
        handleClick();
    }, [
        handleClick
    ]);
    const { data, isLoading, isError } = useAccountMetaQuery();
    const translatedPlaceholderText = useTranslation('forgot_password.email');
    const requiredText = useTranslation('forgot_password.email_required');
    const renderScreen = ()=>{
        if (isLoading) {
            return /*#__PURE__*/ _jsx(PageLoading, {});
        }
        if (isResetEmailSent) {
            return /*#__PURE__*/ _jsx(SuccessPage, {
                email: emailValue
            });
        }
        if (data && !isError) {
            return /*#__PURE__*/ _jsx(Layout, {
                children: /*#__PURE__*/ _jsx(Container, {
                    children: /*#__PURE__*/ _jsxs(Space, {
                        direction: "vertical",
                        size: 16,
                        children: [
                            data && /*#__PURE__*/ _jsx(CompanyLogo, {
                                data: data.company
                            }),
                            /*#__PURE__*/ _jsx(Heading, {
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "forgot_password.page_title"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Form, {
                                form: form,
                                layout: "vertical",
                                onSubmitCapture: onSubmit,
                                onFinish: onSubmit,
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "email",
                                        label: translatedPlaceholderText,
                                        rules: [
                                            {
                                                required: true,
                                                message: requiredText
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            type: "email",
                                            placeholder: translatedPlaceholderText
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(ErrorMessage, {
                                        children: isResetFailed ? 'Something went wrong. Please try again.' : ''
                                    }),
                                    /*#__PURE__*/ _jsx(RecoveryButton, {
                                        loading: resetMailSending,
                                        variant: "primary",
                                        type: "button",
                                        leftIcon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                                        onClick: onSubmit,
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "forgot_password.cta"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(UserInfo, {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "forgot_password.cta_back",
                                            values: {
                                                a: (chunks)=>/*#__PURE__*/ _jsx(Link, {
                                                        to: RoutePaths.login,
                                                        children: /*#__PURE__*/ _jsx(LinkText, {
                                                            children: chunks
                                                        })
                                                    })
                                            }
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                })
            });
        }
        return /*#__PURE__*/ _jsx(_Fragment, {});
    };
    return renderScreen();
};
